import React, { useContext } from 'react';
import { InReceiptItem as DialogInReceiptItem } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/PurchaseConfirmationDialog/components/InReceiptItem';
import { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import {
  StyledSummary,
  StyledSummaryListItemContainer,
  styles,
} from '@apps/registry/common/components/ShoppingCart/ShoppingCartInnerV2/steps/CartReviewStep/components/ReceiptsSection/components/PurchasedOrder/JoyPurchasedOrder/JoyPurchasedOrder.styles';
import { Box, Divider } from '@withjoy/joykit';
import { ButtonV2, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { FormattedCurrency } from '@shared/utils/currency';
import { AnalyticsContext, useHistory, useTranslation } from '@shared/core';
// Separating the below imports as it is used in travel, accommodations and to avoid other components dependency.
import { GiftNoteV2 } from '@apps/registry/common/components/ShoppingCart/components/GiftNote/GiftNoteV2';
import { OrderNumberMetadata } from '@apps/registry/common/components/ShoppingCart/components/MetadataAtoms/OrderNumberMetadata';
import { TrackingMetadata } from '@apps/registry/common/components/ShoppingCart/components/MetadataAtoms/TrackingMetadata';
import { RegistryOrderFragment } from '@graphql/generated';
import { DateFragment } from '@graphql/generated';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  StyledTrackingInfoBox,
  styles as purchasedItemStyles,
} from '@apps/registry/common/components/ShoppingCart/ShoppingCartInnerV2/steps/CartReviewStep/components/ReceiptsSection/components/PurchasedOrder/PurchasedItem/PurchasedItem.styles';
import { Chat } from '@apps/registry/common/components/ShoppingCart/ShoppingCartInnerV2/steps/CartReviewStep/components/ReceiptsSection/components/PurchasedOrder/JoyPurchasedOrder/icons';
import { useGiftWrapECardPrice } from '@shared/components/GiftWrap';
import { CheckOutline } from '@withjoy/joykit/icons';
import { RETURN_POLICY_PATH } from '@apps/registry/common/components/Catalog/Catalog.constants';

import { SalesOrder } from '@apps/registry/common/components/ShoppingCart/ShoppingCartInnerV2/steps/CartReviewStep/components/ReceiptsSection/components/PurchasedOrder/JoyPurchasedOrder/JoyPurchasedOrder.types';
import { calcSubtotal } from '@apps/registry/common/components/ShoppingCart/ShoppingCartInnerV2/steps/CartReviewStep/components/ReceiptsSection/components/PurchasedOrder/JoyPurchasedOrder/JoyPurchasedOrder.utils';
import { useIntercom } from '@shared/core/intercom';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { v4 as uuid } from 'uuid';
import { additionalCostLinesInDisplayOrder } from '@apps/registry/common/utils/additionalCostLines';

type ItemBaseVariant = 'normal' | 'transparent';
type Order = { product: Partial<CookedProduct>; quantity: number; price: string; totalPrice: string; siteName?: string | null };

type JoyPurchasedOrderProps = Partial<RegistryOrderFragment> & {
  orderList: Array<Order>;
  salesOrder: SalesOrder;
  isEdelivery?: boolean;
  brand?: string;
  brandText?: string;
  last4?: string;
  variant?: ItemBaseVariant;
};

type FormatDateOptions = {
  format?: string;
};
export const formatDate = (date: Maybe<DateFragment>, options: FormatDateOptions = {}) => {
  const formatString = options.format || 'MMMM d, yyyy';

  const eventDate = date?.milliseconds || new Date();
  const dateWithTimezoneFormatting = utcToZonedTime(eventDate, date?.timezone || 'America/Los_Angeles');
  return format(dateWithTimezoneFormatting, formatString);
};

export const JoyPurchasedOrder = ({ orderList, salesOrder, isEdelivery, brand, brandText, last4, orderedAt, variant, fulfillment, note, id }: JoyPurchasedOrderProps) => {
  const { t } = useTranslation('sharedRegistry');
  const { t: tRegistry } = useTranslation('guestRegistry');
  const intercom = useIntercom();
  const history = useHistory();
  const { subtotal, delivery, salesTax, total, paidNote, ordered, eCardFee } = t('shoppingCart');
  const { returnPolicy, contactSupport } = t('shoppingCartHelp');
  const { giftPurchasedOrderNumber } = tRegistry('confirmationPage');
  const registryGiftWrapEcardPrice = useGiftWrapECardPrice();
  const giftWrapPriceAsString = `${registryGiftWrapEcardPrice}`;
  const orderDateText = orderedAt ? `${ordered()} ${formatDate(orderedAt)}` : '';
  // we need to calculate this from the salesOrder object
  const hasGiftWrapCard = false; //hasGiftWrapFromSalesOrderPrice(registryGiftWrapEcardPrice, salesOrder);
  const calculatedSubtotalAsString = `${calcSubtotal(salesOrder)}`;
  const additionalCostLines = additionalCostLinesInDisplayOrder(salesOrder);

  const analytics = useContext(AnalyticsContext);

  const handleTelemetry = useEventCallback((name: string, action: string) => {
    analytics.track({
      category: 'guest.purchase',
      action: 'ButtonInteractedClicked',
      extraInfo: { name, action },
    });
  }, []);

  const onContactSupportClick = useEventCallback(() => {
    handleTelemetry('registryContactSupport', 'ButtonInteractedClicked');
    intercom
      .boot()
      .then(() => intercom.show())
      .catch(error => addError(error, { context: 'JoyPurchaseOrder', operation: 'intercom.boot.show' }));
  }, []);

  const onReturnPolicyClick = useEventCallback(() => {
    handleTelemetry('registryReturnPolicy', 'ButtonInteractedClicked');
    history.push(RETURN_POLICY_PATH);
  }, []);

  return (
    <Flex
      flexDirection="column"
      backgroundColor={variant === 'normal' ? 'white' : 'trasparent'}
      paddingTop={variant === 'normal' ? 6 : 0}
      paddingBottom={variant === 'normal' ? 6 : 0}
    >
      {orderDateText && (
        <TextV2
          typographyVariant="hed2"
          paddingLeft={5}
        >
          {orderDateText}
        </TextV2>
      )}
      <StyledTrackingInfoBox __css={purchasedItemStyles.trackingInfoBox}>
        {fulfillment?.orderNumber && <OrderNumberMetadata orderNumber={fulfillment.orderNumber} />}
        {fulfillment?.shipments?.[0]?.trackingInformation?.trackingNumber && (
          <Box marginTop={fulfillment?.orderNumber ? 6 : 0}>
            <TrackingMetadata
              trackingNumber={fulfillment.shipments[0].trackingInformation.trackingNumber}
              shippingCarrier={fulfillment.shipments[0].trackingInformation.company || undefined}
            />
          </Box>
        )}
      </StyledTrackingInfoBox>
      {id && note && (
        <GiftNoteV2
          note={note}
          orderId={id}
          variant="solid"
        />
      )}
      <Flex
        alignItems="center"
        marginLeft={3}
      >
        <CheckOutline
          size={32}
          color="positive6"
        />
        <TextV2
          marginLeft={3}
          typographyVariant="hed2"
        >
          {giftPurchasedOrderNumber({ orderId: salesOrder?.id })}
        </TextV2>
      </Flex>
      {orderList?.length > 0 &&
        orderList.map(({ product, quantity, price, totalPrice, siteName }, idx) => (
          <DialogInReceiptItem
            key={idx}
            product={product}
            quantity={quantity}
            price={price}
            totalPrice={totalPrice}
            isDonationFundItem={false}
            siteName={siteName}
            compact
            variant={variant || 'transparent'}
            productPhotoStyles={{ height: 9, width: 9 }}
            sm
          />
        ))}
      <StyledSummary __css={styles.summaryStyles}>
        <Divider />
        <SpacingStack
          spacing={3}
          marginY={6}
        >
          <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
            <TextV2 typographyVariant="body1">{subtotal()}</TextV2>
            <TextV2
              typographyVariant="body1"
              color="mono10"
            >
              <FormattedCurrency
                priceFloatingPointDecimalString={calculatedSubtotalAsString}
                formatForm="short"
              />
            </TextV2>
          </StyledSummaryListItemContainer>
          <Divider />
          {hasGiftWrapCard && (
            <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
              <TextV2 typographyVariant="body1">{eCardFee()}</TextV2>
              <TextV2
                typographyVariant="body1"
                color="mono10"
              >
                <FormattedCurrency
                  priceFloatingPointDecimalString={giftWrapPriceAsString}
                  priceCurrencyCode="USD"
                  formatForm="short"
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          )}
          {hasGiftWrapCard && <Divider />}
          {!isEdelivery && (
            <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
              <TextV2 typographyVariant="body1">{delivery()}</TextV2>
              <TextV2
                typographyVariant="body1"
                color="mono10"
              >
                <FormattedCurrency
                  priceFloatingPointDecimalString={salesOrder?.shippingAndHandlingTotal?.floatingPointDecimalString ?? '0'}
                  priceCurrencyCode={salesOrder?.shippingAndHandlingTotal?.currency?.code}
                  formatForm="short"
                  canShowFreeLabel
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          )}
          {!isEdelivery && <Divider />}
          {additionalCostLines.map(line => (
            <StyledSummaryListItemContainer
              key={`costLine-${uuid()}`}
              __css={styles.summaryListItemContainerStyles}
            >
              <TextV2 typographyVariant="body1">{line.title}</TextV2>
              <TextV2
                typographyVariant="body1"
                color="mono10"
              >
                <FormattedCurrency
                  priceFloatingPointDecimalString={line.lineValue?.floatingPointDecimalString ?? '0'}
                  priceCurrencyCode="USD"
                  formatForm="short"
                />
              </TextV2>
            </StyledSummaryListItemContainer>
          ))}
          {additionalCostLines.length > 0 && <Divider />}
          <StyledSummaryListItemContainer __css={styles.summaryListItemContainerStyles}>
            <TextV2 typographyVariant="body1">{salesTax()}</TextV2>
            <TextV2
              typographyVariant="body1"
              color="mono10"
            >
              <FormattedCurrency
                priceFloatingPointDecimalString={salesOrder?.taxesTotal?.floatingPointDecimalString ?? '0'}
                priceCurrencyCode={salesOrder?.taxesTotal?.currency?.code}
                formatForm="short"
              />
            </TextV2>
          </StyledSummaryListItemContainer>
        </SpacingStack>
        <Divider />
        <StyledSummaryListItemContainer
          __css={styles.summaryListItemContainerStyles}
          paddingY={4}
        >
          <TextV2 typographyVariant="hed1">{total()}</TextV2>
          <TextV2 typographyVariant="hed3">
            <FormattedCurrency
              priceFloatingPointDecimalString={salesOrder?.total?.floatingPointDecimalString ?? '0'}
              priceCurrencyCode={salesOrder?.total?.currency?.code}
              formatForm="short"
            />
          </TextV2>
        </StyledSummaryListItemContainer>
        <Flex>
          {brand && last4 && (
            <TextV2
              typographyVariant="hed1"
              color="mono12"
            >
              {paidNote({ card: brandText, cardLastDigits: last4 })}
            </TextV2>
          )}
        </Flex>
      </StyledSummary>
      {id && !note && (
        <GiftNoteV2
          note={note}
          orderId={id}
          variant="solid"
        />
      )}
      <ButtonV2
        typographyVariant="button2"
        intent="neutral"
        variant="ghost"
        fontWeight={600}
        marginBottom={6}
        marginX={5}
        onClick={onContactSupportClick}
      >
        <Flex marginRight={1}>
          <Chat />
        </Flex>
        {contactSupport()}
      </ButtonV2>
      <ButtonV2
        typographyVariant="button2"
        textDecoration="underline"
        intent="neutral"
        variant="link"
        marginX={5}
        onClick={onReturnPolicyClick}
      >
        <Flex flexDirection="column">{returnPolicy()}</Flex>
      </ButtonV2>
    </Flex>
  );
};
